import CloudOffOutlined from "@mui/icons-material/CloudOffOutlined";
import { Box, CircularProgress } from "@mui/material";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../../../../../components/UIToolkit/molecules/EmptyState";
import { useChosenDepartment } from "../../../../../../contexts/ChosenDepartment";
import { useDebouncedSave } from "../../../../../../hooks/useDebouncedSave";
import { useErrorSnackbar } from "../../../../../../hooks/useErrorSnackbar";
import { useAllJobs, useJobs } from "../../../../../../hooks/useJobs";
import { hrisEmployeeName } from "../../../../../../utils/formatters";
import {
	type CertificationJobMapping,
	type CertPrimaryJobChangePreview,
	HrisRawSyncStatus,
	type JobNotEnabledEmployee,
	type NurseRole,
	Position,
} from "../../../../../../utils/models";
import { isUnknownJobId } from "../../jobGroups";
import { useDerivePosition } from "../derivedPosition";
import {
	buildJobIds,
	useCertPrimaryJobChangePreviewFetcher,
	useDepartmentCertificationMapping,
	useHrisEmployeeShiftStats,
	useHrisRawEmployees,
	useJobNotEnabledEmployees,
	useUpsertCertMapping,
} from "../hrisMappingHooks";
import { JobMappingChangeConfirmDialog } from "../JobMappingChangeConfirmDialog";
import { normalizeRoleString } from "../utils";
import { DontSyncConfirmDialog, type DontSyncEmployee } from "./DontSyncConfirmDialog";
import type { JobNotEnabledMark } from "./EmployeeSyncStatusIcon";
import { HrisJobOverview } from "./HrisJobOverview";
import { HrisRawEmployees } from "./HrisRawEmployees";
import type { JobPickerChange } from "./JobPickerCell";
import { type SyncEmployee, SyncEmployeesConfirmDialog } from "./SyncEmployeesConfirmDialog";
import { type RoleStringRow, useRoleStringRows } from "./useRoleStringRows";

/** Stable identity for the not-yet-loaded case, so the left pane's memos don't re-run every render. */
const EMPTY_JOB_NOT_ENABLED: JobNotEnabledEmployee[] = [];

/** A DontSync→job change awaiting employee-sync selection: the role string plus the mapping it will switch to. */
interface SyncTarget {
	roleString: string;
	jobId: string;
	position: Position;
	role: NurseRole;
	employees: SyncEmployee[];
}

/**
 * Pretty Mapping view of the HRIS Mapping screen.
 *
 * Layout: a 2-column flex row.
 * - Left pane: raw HRIS employees grouped by internal_department -> role_string with a per-role-string job picker.
 * - Right pane: read-only summary of the resolved HRIS-job -> in-house-job mapping.
 *
 * Owns the upsert handler shared with both panes; persists via the existing `useUpsertCertMapping`
 * hook with debouncing through `useDebouncedSave` (matches the Raw Table grid's UX).
 */
export const HrisJobAlignment: FC = () => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const departmentId = chosenDepartment.department_id;
	const { data: mappings, isLoading: isLoadingMapping } = useDepartmentCertificationMapping(departmentId);
	const { data: rawEmployees, isLoading: isLoadingRaw } = useHrisRawEmployees(departmentId);
	const { data: shiftStats, isLoading: isLoadingStats } = useHrisEmployeeShiftStats(departmentId);
	// DAT-834. Always enabled here: this pane only renders for a saved department.
	const { data: jobNotEnabledEmployees } = useJobNotEnabledEmployees(departmentId, true);
	const jobs = useJobs();
	// The whole catalog, not the department's enabled jobs: the no-job sentinel is by definition not
	// enabled anywhere, and it is the row whose advice differs. Shares the app-wide ["jobs"] cache.
	const { data: allJobs = [] } = useAllJobs();
	const upsertMutation = useUpsertCertMapping(departmentId);
	const fetchPrimaryJobChangePreview = useCertPrimaryJobChangePreviewFetcher(departmentId);
	// Tracks role_strings that have a debounced save scheduled or in flight. Both panes render
	// a JumpingDots indicator next to rows whose key is in this set — matches the Raw Table UX.
	const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
	// Pending Don't Sync change awaiting confirmation when the role string still has active employees.
	const [dontSyncTarget, setDontSyncTarget] = useState<{ roleString: string; employees: DontSyncEmployee[] } | null>(null);
	// Pending DontSync→job change awaiting employee-sync selection.
	const [syncTarget, setSyncTarget] = useState<SyncTarget | null>(null);
	// Pending primary-job change on an existing mapping awaiting the DAT-535 confirm popup (with its preview).
	const [jobChangeTarget, setJobChangeTarget] = useState<{
		roleString: string;
		newJobId: string;
		position: Position;
		role: NurseRole;
		currentJobLabel: string;
		preview: CertPrimaryJobChangePreview;
	} | null>(null);

	const rows = useRoleStringRows(mappings, rawEmployees);
	const statsByEmployeeId = useMemo(() => new Map((shiftStats ?? []).map((s) => [s.employeeId, s])), [shiftStats]);
	// Same join key as `statsByEmployeeId`, for the same reason: a raw HRIS row carries no nurse id.
	// Rows with no HRIS record (`employeeId === null`) drop out of the map and are counted separately
	// by the pane's banner, so a name that cannot be marked is still reported somewhere.
	const jobNotEnabledByEmployeeId = useMemo(() => {
		const byEmployee = new Map<string, JobNotEnabledMark>();
		for (const employee of jobNotEnabledEmployees ?? []) {
			// Resolved here, once per response, rather than inside the icon: the catalog lookup is the
			// same for every row, and the icon renders per employee.
			if (employee.employeeId !== null) byEmployee.set(employee.employeeId, { ...employee, hasNoJob: isUnknownJobId(employee.jobId, allJobs) });
		}
		return byEmployee;
	}, [jobNotEnabledEmployees, allJobs]);
	// DAT-804: the admin no longer picks a position; the still-required wire field is derived.
	// Gated below with the rest: the derivation reads these defaults, so a save made before they land
	// would write the column's server default over what the global table actually says. A REJECTED load
	// gets its own gate rather than riding the spinner, because it is indistinguishable from a successful
	// empty one here — `isLoading` goes false with no data, and the error snackbar does not stop a save.
	// The status comes from `useDerivePosition` and NOT a second `useCertificationDefaults()` — two
	// observers of the one cache entry turned a single failed load into two identical error snackbars.
	const { derivePositionFor, isLoading: isLoadingCertDefaults, isError: isErrorCertDefaults } = useDerivePosition();

	const [search, setSearch] = useState("");

	// Normalized role-string keys whose row matches the query (by role string OR by employee name).
	// Returns null when the search is empty so the right-pane filter can short-circuit.
	const matchingKeys = useMemo<Set<string> | null>(() => {
		const q = search.trim().toLowerCase();
		if (!q) return null;
		const set = new Set<string>();
		for (const row of rows) {
			if (row.roleString.toLowerCase().includes(q)) {
				set.add(normalizeRoleString(row.roleString));
				continue;
			}
			if (row.rawEmployees.some((e) => hrisEmployeeName(e).toLowerCase().includes(q))) {
				set.add(normalizeRoleString(row.roleString));
			}
		}
		return set;
	}, [rows, search]);

	// Right pane = filtered cert mappings (same source/filter as the Raw Table) PLUS synthetic
	// "not mapped" rows for raw role strings with no cert mapping at all. Synthetic rows carry
	// `job: null`, `position: null` so the cell renderer shows the "Not mapped" chip — the
	// DONT_SYNC chip is reserved for rows where `position === Position.DontSync`.
	const rawRoleSet = useMemo(() => new Set((rawEmployees ?? []).map((e) => normalizeRoleString(e.role))), [rawEmployees]);
	const overviewMappings = useMemo<CertificationJobMapping[]>(() => {
		const filtered = (mappings ?? []).filter((m) => m.isCustom || rawRoleSet.has(normalizeRoleString(m.roleString)));
		const mappedKeys = new Set(filtered.map((m) => normalizeRoleString(m.roleString)));
		const orphanByKey = new Map<string, string>();
		for (const e of rawEmployees ?? []) {
			const key = normalizeRoleString(e.role);
			if (!key || mappedKeys.has(key) || orphanByKey.has(key)) continue;
			orphanByKey.set(key, e.role);
		}
		const orphans: CertificationJobMapping[] = Array.from(orphanByKey.values()).map((roleString) => ({
			roleString,
			jobs: [],
			position: null,
			role: null,
			isOverride: false,
			isCustom: false,
			defaultDisplay: null,
		}));
		return [...filtered, ...orphans];
	}, [mappings, rawEmployees, rawRoleSet]);

	// Right-pane rows must respect the same search the left pane uses, so a query that
	// hits a role string OR an employee name keeps the corresponding cert mapping visible on the right.
	const filteredOverviewMappings = useMemo<CertificationJobMapping[]>(() => {
		if (!matchingKeys) return overviewMappings;
		return overviewMappings.filter((m) => matchingKeys.has(normalizeRoleString(m.roleString)));
	}, [overviewMappings, matchingKeys]);

	const scheduleSave = useDebouncedSave<{
		roleString: string;
		jobIds: string[];
		position: string;
		role: string | null;
		deactivateNurseIds?: number[];
		syncNurseIds?: number[];
	}>((payload) => {
		upsertMutation.mutate(payload, {
			onError: (err) => showErrorSnackbar(t("hris.mapping.saveError"), {}, err),
			onSettled: () =>
				setPendingKeys((prev) => {
					const next = new Set(prev);
					next.delete(payload.roleString);
					return next;
				}),
		});
	});

	const markPending = useCallback((roleString: string) => setPendingKeys((prev) => new Set(prev).add(roleString)), []);
	const clearPending = useCallback(
		(roleString: string) =>
			setPendingKeys((prev) => {
				const next = new Set(prev);
				next.delete(roleString);
				return next;
			}),
		[],
	);

	// Active employees under this role string, deduped by nurse — the deactivation candidates.
	const buildActiveEmployees = useCallback(
		(row: RoleStringRow): DontSyncEmployee[] => {
			const byNurse = new Map<number, DontSyncEmployee>();
			for (const emp of row.rawEmployees) {
				// Mirror countSyncingEmployees exactly: only Synced employees linked to a still-active
				// nurse are candidates, so the count and this list never disagree (see HrisRawEmployees).
				const stats = statsByEmployeeId.get(emp.employeeId);
				if (emp.syncStatus !== HrisRawSyncStatus.Synced || !stats || !stats.active || byNurse.has(stats.nurseId)) continue;
				byNurse.set(stats.nurseId, {
					nurseId: stats.nurseId,
					name: hrisEmployeeName(emp),
					actualShiftsLast30: stats.actualShiftsLast30,
					scheduledShiftsLast30: stats.scheduledShiftsLast30,
				});
			}
			return Array.from(byNurse.values());
		},
		[statsByEmployeeId],
	);

	// All employees under this role string for the re-sync dialog: linked nurses (deduped, selectable) plus
	// not-yet-linked ones (nurseId null → shown for context, sync on next run regardless).
	const buildSyncEmployees = useCallback(
		(row: RoleStringRow): SyncEmployee[] => {
			const out: SyncEmployee[] = [];
			const seenNurse = new Set<number>();
			for (const emp of row.rawEmployees) {
				const stats = statsByEmployeeId.get(emp.employeeId);
				if (stats && seenNurse.has(stats.nurseId)) continue;
				if (stats) seenNurse.add(stats.nurseId);
				out.push({
					key: emp.employeeId,
					nurseId: stats?.nurseId ?? null,
					name: hrisEmployeeName(emp),
					actualShiftsLast30: stats?.actualShiftsLast30 ?? 0,
					scheduledShiftsLast30: stats?.scheduledShiftsLast30 ?? 0,
				});
			}
			return out;
		},
		[statsByEmployeeId],
	);

	// The full ordered jobIds list for an upsert, resolved from the row's current jobs.
	// Every upsert payload carries the full list; the backend diffs the tail onto linked nurses.
	const rowJobIds = useCallback(
		(roleString: string, firstJobId: string) => {
			const row = rows.find((r) => r.roleString === roleString);
			return buildJobIds(firstJobId, row?.jobs ?? []);
		},
		[rows],
	);

	const handleConfirmSync = useCallback(
		(syncNurseIds: number[]) => {
			if (!syncTarget) return;
			markPending(syncTarget.roleString);
			scheduleSave(syncTarget.roleString, {
				roleString: syncTarget.roleString,
				jobIds: rowJobIds(syncTarget.roleString, syncTarget.jobId),
				position: syncTarget.position,
				role: syncTarget.role,
				syncNurseIds,
			});
			setSyncTarget(null);
		},
		[syncTarget, markPending, scheduleSave, rowJobIds],
	);

	const saveDontSync = useCallback(
		(roleString: string, deactivateNurseIds: number[]) => {
			markPending(roleString);
			scheduleSave(roleString, {
				roleString,
				// Don't-Sync rows carry no jobs — clearing the list also strips the extra jobs from linked nurses.
				jobIds: [],
				position: Position.DontSync,
				role: null,
				...(deactivateNurseIds.length ? { deactivateNurseIds } : {}),
			});
		},
		[markPending, scheduleSave],
	);

	const handleConfirmDontSync = useCallback(
		(deactivateNurseIds: number[]) => {
			if (!dontSyncTarget) return;
			saveDontSync(dontSyncTarget.roleString, deactivateNurseIds);
			setDontSyncTarget(null);
		},
		[dontSyncTarget, saveDontSync],
	);

	// Confirmed selection from the AdditionalJobsDialog — the row's first job and role ride along
	// unchanged (position derived) so the upsert stays a full-row write (DAT-466).
	const handleAdditionalJobsChange = useCallback(
		(roleString: string, additionalJobIds: string[]) => {
			const row = rows.find((r) => r.roleString === roleString);
			const syncJob = row?.jobs[0];
			if (!row || !syncJob || !row.role) {
				// Defensive: the dialog is only reachable from mapped, non-DontSync rows.
				return;
			}
			markPending(roleString);
			scheduleSave(roleString, {
				roleString,
				jobIds: [syncJob.id, ...additionalJobIds],
				position: derivePositionFor(roleString, row.position),
				role: row.role,
			});
		},
		[rows, markPending, scheduleSave, derivePositionFor],
	);

	const saveJobChange = useCallback(
		(roleString: string, jobId: string, position: Position, role: NurseRole) => {
			markPending(roleString);
			scheduleSave(roleString, { roleString, jobIds: rowJobIds(roleString, jobId), position, role });
		},
		[markPending, scheduleSave, rowJobIds],
	);

	// Fetch the preview of active linked employees, then either save straight through (nobody to re-job)
	// or open the DAT-535 confirm dialog. The row shows the pending spinner while the preview is in flight,
	// so the picker doesn't sit silently between the click and the dialog. On failure we surface the error
	// and save nothing.
	const openJobChangeConfirm = useCallback(
		async (roleString: string, newJobId: string, position: Position, role: NurseRole, currentJobLabel: string) => {
			markPending(roleString);
			try {
				const preview = await fetchPrimaryJobChangePreview(roleString, newJobId);
				if (preview.rows.length === 0) {
					// saveJobChange re-asserts the pending key and owns its lifecycle through onSettled.
					saveJobChange(roleString, newJobId, position, role);
					return;
				}
				clearPending(roleString);
				setJobChangeTarget({ roleString, newJobId, position, role, currentJobLabel, preview });
			} catch (err) {
				clearPending(roleString);
				showErrorSnackbar(t("hris.mapping.jobChangePreview.error"), {}, err);
			}
		},
		[fetchPrimaryJobChangePreview, saveJobChange, markPending, clearPending, showErrorSnackbar, t],
	);

	const handleJobChange = useCallback(
		(roleString: string, change: JobPickerChange) => {
			if (change.kind === "dontSync") {
				// Confirm first if active employees are still synced under this role string.
				const targetRow = rows.find((r) => r.roleString === roleString);
				const activeEmployees = targetRow ? buildActiveEmployees(targetRow) : [];
				if (activeEmployees.length > 0) {
					setDontSyncTarget({ roleString, employees: activeEmployees });
					return;
				}
				saveDontSync(roleString, []);
				return;
			}
			// kind === "job"
			// The dialog supplies the role only when the row cannot (DontSync/orphan/role-less);
			// otherwise the row's own role rides along. Position is always derived (DAT-804).
			const row = rows.find((r) => r.roleString === roleString);
			const position = derivePositionFor(roleString, row?.position);
			const role: NurseRole | null = change.role ?? row?.role ?? null;
			if (!role) {
				// Defensive: should not happen — the picker collects the role whenever the row lacks one.
				return;
			}
			// Transitioning into sync (picker supplied the role) with linked nurses → let the admin pick whom
			// to sync first.
			if (change.role && row) {
				const employees = buildSyncEmployees(row);
				if (employees.some((e) => e.nurseId !== null)) {
					setSyncTarget({ roleString, jobId: change.jobId, position, role, employees });
					return;
				}
			}
			// Plain job→job reassignment on an existing mapping: if the primary (first) job actually changes,
			// confirm the propagation to linked employees first (DAT-535). A first-time primary (no current
			// job) or an unchanged selection saves straight through.
			const currentPrimary = row?.jobs[0];
			if (row && currentPrimary && change.jobId !== currentPrimary.id) {
				const currentJobLabel = currentPrimary.displayName ?? currentPrimary.name ?? t("hris.mapping.jobChangeConfirm.noJob");
				void openJobChangeConfirm(roleString, change.jobId, position, role, currentJobLabel);
				return;
			}
			saveJobChange(roleString, change.jobId, position, role);
		},
		[rows, buildSyncEmployees, buildActiveEmployees, saveDontSync, openJobChangeConfirm, saveJobChange, derivePositionFor, t],
	);

	const handleConfirmJobChange = useCallback(() => {
		if (!jobChangeTarget) return;
		saveJobChange(jobChangeTarget.roleString, jobChangeTarget.newJobId, jobChangeTarget.position, jobChangeTarget.role);
		setJobChangeTarget(null);
	}, [jobChangeTarget, saveJobChange]);

	// Wait for shift stats too: the sync-status icons and the Don't-Sync deactivation candidates both
	// derive from them, so rendering before they load would briefly show wrong counts and could let a
	// Don't-Sync save commit with an empty deactivation list.
	if (isLoadingMapping || isLoadingRaw || isLoadingStats || isLoadingCertDefaults) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
				<CircularProgress />
			</Box>
		);
	}

	// Every job pick on this pane derives a position, so without the defaults the pane can only write
	// `Nursing`. Blocking beats a read-only pane: the one thing it is for is editing these mappings.
	if (isErrorCertDefaults) {
		return <EmptyState icon={<CloudOffOutlined />} title={t("department.config.certDefaults.loadError")} dataTestId="hris-mapping-cert-defaults-error" />;
	}

	// Nothing synced and nothing mapped → one centered empty state instead of two blank panes.
	if ((rawEmployees?.length ?? 0) === 0 && overviewMappings.length === 0) {
		return (
			<EmptyState
				icon={<CloudOffOutlined />}
				title={t("hris.mapping.empty.title")}
				hint={t("hris.mapping.empty.hint")}
				dataTestId="hris-mapping-job-alignment-empty"
			/>
		);
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "row", gap: 2, height: "100%", overflow: "hidden" }} data-testid="hris-mapping-job-alignment">
			<Box sx={{ flex: "0 0 520px", minWidth: 0, height: "100%" }}>
				<HrisRawEmployees
					rows={rows}
					jobs={jobs ?? []}
					pendingKeys={pendingKeys}
					onJobChange={handleJobChange}
					onAdditionalJobsChange={handleAdditionalJobsChange}
					statsByEmployeeId={statsByEmployeeId}
					jobNotEnabledByEmployeeId={jobNotEnabledByEmployeeId}
					departmentJobNotEnabled={jobNotEnabledEmployees ?? EMPTY_JOB_NOT_ENABLED}
					search={search}
					setSearch={setSearch}
				/>
			</Box>
			<Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
				<HrisJobOverview mappings={filteredOverviewMappings} pendingKeys={pendingKeys} />
			</Box>
			<DontSyncConfirmDialog
				open={!!dontSyncTarget}
				roleString={dontSyncTarget?.roleString ?? ""}
				employees={dontSyncTarget?.employees ?? []}
				onConfirm={handleConfirmDontSync}
				onClose={() => setDontSyncTarget(null)}
			/>
			<SyncEmployeesConfirmDialog
				open={!!syncTarget}
				roleString={syncTarget?.roleString ?? ""}
				employees={syncTarget?.employees ?? []}
				onConfirm={handleConfirmSync}
				onClose={() => setSyncTarget(null)}
			/>
			{jobChangeTarget && (
				<JobMappingChangeConfirmDialog
					open
					key={jobChangeTarget.roleString}
					roleString={jobChangeTarget.roleString}
					currentJobLabel={jobChangeTarget.currentJobLabel}
					newJobLabel={jobChangeTarget.preview.newJob.displayName ?? jobChangeTarget.preview.newJob.name}
					rows={jobChangeTarget.preview.rows}
					onConfirm={handleConfirmJobChange}
					onClose={() => setJobChangeTarget(null)}
				/>
			)}
		</Box>
	);
};
